/**
 * #613 #649, round 12 #727, R-13 kit, R-16 v1.2 change 01 — the L0 platform bar on the kit's L0Bar:
 * Bog (#061D21 dark) in both themes, 56px, the platform's wordmark at 24px (the lockup uploaded
 * through /api/meta — the dark-surface file, because the bar is dark in both themes; the display name
 * when none is uploaded — never compiled in), the platform entries the declaration gives the signed-in
 * user (the current one in bar ink with the accent underline), the search box filling the bar
 * ("Search <platform name>", opening the command palette) and the tools. An entry the role cannot use
 * is absent, never disabled.
 */
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { L0Bar, L0Search, MenuBarIntent, OPEN_PALETTE_EVENT } from '@/kit';
import { platformEntries } from '@/app/platform';
import { L0Entry } from './L0Menus';
import { useMeta } from '@/app/meta';
import { logoPair } from '@/components/BrandLogo';
import type { MenuDecl, MenuItemDecl, PublishedApp } from '@/features/apps/apps-api';


export function PlatformBar({
  platformName,
  entries,
  roles,
  children,
  apps = [],
  dataSections = [],
  adminGroups = [],
}: {
  platformName: string;
  entries: readonly MenuItemDecl[];
  roles: readonly string[];
  children?: ReactNode;
  /** R-16 T-135: what the Apps ▾, Data ▾ and Admin ▾ menus list (GET /api/apps, already cut to the person) */
  apps?: readonly PublishedApp[];
  dataSections?: readonly MenuItemDecl[];
  adminGroups?: readonly MenuDecl[];
}) {
  const { logos, platformShortName } = useMeta();
  // the bar is dark in both modes: the lockup's dark variant (for the dark surface)
  const lockup = logoPair(logos, 'lockup');
  return (
    <L0Bar
      mark={
        <NavLink to="/apps" className="k-l0-mark text-bar-ink" data-testid="platform-wordmark" aria-label={`${platformName} — Apps`}>
          {lockup ? <img src={lockup.dark} alt={`${platformName} logo`} /> : <span>{platformName}</span>}
        </NavLink>
      }
      nav={
        <nav aria-label="Platform" data-testid="platform-nav" className="k-l0-nav">
          {/* R-16 T-135: Apps, Data and Admin keep their links and gain a ▾ menu; one menu open at a time */}
          <MenuBarIntent>
            {platformEntries(entries, roles).map((e) => (
              <L0Entry key={e.id} entry={e} sources={{ apps, dataSections, adminGroups }} />
            ))}
          </MenuBarIntent>
        </nav>
      }
      // v1.2: the search opens the command palette (Ctrl K / ⌘K) for every tier; Find anything (`shell.find`) is an entry in it
      search={<L0Search label={`Search ${platformShortName?.trim() || platformName}`} data-testid="platform-search" onOpen={() => window.dispatchEvent(new Event(OPEN_PALETTE_EVENT))} />}
      right={children}
    />
  );
}
